import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { X, Lightbulb, ArrowRight, Hourglass } from 'lucide-react';
import { EtatJeu, GameData, Maj, Personnage } from '../game/types';
import { assetUrl } from '../game/logic';

interface Props {
  data: GameData;
  etat: EtatJeu;
  maj: Maj;
  onAllerTatoues: () => void;
}

const PortraitCadre = ({ p, grand = false }: { p: Personnage; grand?: boolean }) => {
  const initiale = p.nom.split(' ').slice(-1)[0][0];
  if (p.portrait) {
    return (
      <img
        src={assetUrl(p.portrait)}
        alt={p.nom}
        className="w-full aspect-[3/4] object-cover border border-encre"
      />
    );
  }
  return (
    <div className="w-full aspect-[3/4] bg-casse border border-encre flex flex-col items-center justify-center gap-1">
      <span className={`font-script text-encre ${grand ? 'text-8xl' : 'text-4xl'}`}>{initiale}</span>
      {grand && <span className="petit-cap">{p.annees}</span>}
    </div>
  );
};

export default function Galerie({ data, etat, maj, onAllerTatoues }: Props) {
  const [fiche, setFiche] = useState<Personnage | null>(null);
  const [maintenant, setMaintenant] = useState(Date.now());

  const question = data.questions[etat.questionsOk.length];
  const verrouille = !!etat.verrouJusqua && etat.verrouJusqua > maintenant;

  useEffect(() => {
    if (etat.verrouJusqua && etat.verrouJusqua > Date.now()) {
      const t = window.setInterval(() => setMaintenant(Date.now()), 500);
      return () => window.clearInterval(t);
    }
  }, [etat.verrouJusqua]);

  const repondre = (idx: number) => {
    if (!question || verrouille) return;
    if (idx === question.bonneReponse) {
      toast.success('Fort bien ! Deux tatoués se dévoilent.');
      maj((e) => ({ ...e, questionsOk: [...e.questionsOk, question.id], verrouJusqua: null }));
    } else {
      toast.error("Ce n'est point cela. Quinze secondes de patience.");
      maj((e) => ({
        ...e,
        erreursQuestion: { ...e.erreursQuestion, [question.id]: (e.erreursQuestion[question.id] || 0) + 1 },
        verrouJusqua: Date.now() + 15000,
      }));
      setMaintenant(Date.now());
    }
  };

  const prendreCoupDePouce = () => {
    if (!question) return;
    maj((e) => ({
      ...e,
      penalitesSec: e.penalitesSec + 60,
      coupsDePouce: [...e.coupsDePouce, question.id],
    }));
    toast('Coup de pouce accordé — 60 secondes s\'ajoutent au chrono.');
  };

  const erreurs = question ? etat.erreursQuestion[question.id] || 0 : 0;
  const coupPris = question ? etat.coupsDePouce.includes(question.id) : false;
  const resteVerrou = etat.verrouJusqua ? Math.max(0, Math.ceil((etat.verrouJusqua - maintenant) / 1000)) : 0;

  return (
    <div className="space-y-8">
      <section className="anim-apparition" data-testid="question-card">
        <p className="petit-cap">Acte I — La galerie</p>
        {question ? (
          <>
            <p className="text-sm text-encre-clair mt-2">Question {etat.questionsOk.length + 1} sur 5</p>
            <h2 className="text-lg font-medium text-texte leading-relaxed mt-2">{question.intitule}</h2>

            {verrouille && (
              <div className="mt-4 border border-erreur rounded-lg px-4 py-3 flex items-center gap-3 text-erreur text-sm" data-testid="verrou-notice">
                <Hourglass size={18} />
                <span>La galerie vous fait patienter… {resteVerrou} s</span>
              </div>
            )}

            <div className="mt-4 space-y-3">
              {(etat.ordresPropositions[question.id] || [0, 1, 2, 3]).map((idx, i) => (
                <button
                  key={idx}
                  data-testid={`proposition-btn-${i}`}
                  disabled={verrouille}
                  onClick={() => repondre(idx)}
                  className="w-full min-h-[52px] px-5 py-3 text-left rounded-lg border border-encre bg-white text-texte font-medium transition-colors duration-200 active:bg-petale disabled:opacity-40"
                >
                  {question.propositions[idx]}
                </button>
              ))}
            </div>

            {erreurs >= 2 && !coupPris && (
              <button
                data-testid="coup-de-pouce-btn"
                onClick={prendreCoupDePouce}
                className="mt-4 w-full min-h-[48px] px-5 rounded-full border border-olive text-olive font-medium flex items-center justify-center gap-2 transition-colors duration-200 active:bg-casse"
              >
                <Lightbulb size={18} /> Coup de pouce (+60 s)
              </button>
            )}
            {coupPris && (
              <p className="mt-4 text-sm italic text-olive border-l-2 border-olive pl-3" data-testid="coup-de-pouce-texte">
                {question.coupDePouce}
              </p>
            )}
          </>
        ) : (
          <div className="mt-3 carte px-5 py-6">
            <p className="text-texte leading-relaxed">
              La galerie n'a plus de secrets pour vous. Les dix tatoués vous attendent — courez !
            </p>
            <button data-testid="aller-tatoues-btn" className="btn-principal mt-4" onClick={onAllerTatoues}>
              Voir les tatoués <ArrowRight size={18} />
            </button>
          </div>
        )}
      </section>

      <section>
        <div className="flex items-end justify-between">
          <h3 className="petit-cap">Les neuf figures du château</h3>
        </div>
        <p className="text-sm text-encre-clair mt-1">
          Consultez les fiches librement — et allez voir les vrais tableaux.
        </p>
        <div className="grid grid-cols-3 gap-3 mt-4">
          {data.personnages.map((p) => (
            <button
              key={p.id}
              data-testid={`fiche-personnage-${p.id}`}
              onClick={() => setFiche(p)}
              className="text-left transition-transform duration-200 active:scale-[0.97]"
            >
              <PortraitCadre p={p} />
              <p className="text-xs font-medium text-encre mt-1.5 leading-tight">{p.nom}</p>
            </button>
          ))}
        </div>
      </section>

      {fiche && (
        <div className="fixed inset-0 z-40 bg-white overflow-y-auto anim-apparition" data-testid="fiche-personnage-overlay">
          <div className="max-w-md mx-auto px-6 py-6">
            <button
              data-testid="fermer-fiche-btn"
              onClick={() => setFiche(null)}
              className="min-h-[44px] min-w-[44px] flex items-center gap-2 text-encre-clair font-medium"
            >
              <X size={20} /> Fermer
            </button>
            <div className="mt-4 max-w-[260px] mx-auto">
              <PortraitCadre p={fiche} grand />
            </div>
            <h2 className="font-script text-5xl text-encre text-center mt-6">{fiche.nom}</h2>
            <p className="petit-cap text-center mt-1">{fiche.annees}</p>
            <p className="text-texte leading-relaxed mt-6">{fiche.bio}</p>
            <div className="h-px bg-olive/50 my-6" />
            <p className="petit-cap">Anecdote</p>
            <p className="italic text-texte leading-relaxed mt-2">{fiche.anecdote}</p>
          </div>
        </div>
      )}
    </div>
  );
}
